import { useId, useState } from "react";
import { motion, type Transition } from "motion/react";
import { cn } from "../cn";

export type LogoVariant = "full" | "horizontal" | "stacked" | "mark";
export type LogoColor = "primary" | "monochrome" | "gradient" | "white";

export interface OneNextyLogoProps extends React.SVGProps<SVGSVGElement> {
  /** Variante d'affichage (full: mark+text, horizontal: layout en ligne, stacked: centré, mark: symbole seul) */
  variant?: LogoVariant;
  /** Palette de couleur du logo */
  color?: LogoColor;
  /** Hauteur indicative du logo en pixels (défaut: 36px pour full, 32px pour mark) */
  size?: number;
  /** Titre optionnel d'accessibilité */
  title?: string;
  className?: string;
}

const springTransition: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 22,
};

/**
 * Symbole / Monogramme Corporate One Nexty (Monogramme 1N Fusionné & Shield Géométrique)
 */
export function OneNextyMark({
  color = "primary",
  size = 36,
  title = "One Nexty Corporate Monogram",
  className,
  ...props
}: Omit<OneNextyLogoProps, "variant">) {
  const gradientId = useId();
  const glowId = useId();
  const bgGradientId = useId();

  const isGradient = color === "gradient" || color === "primary";
  const isMono = color === "monochrome";
  const isWhite = color === "white";

  const strokeColor = isWhite
    ? "#FFFFFF"
    : isMono
    ? "currentColor"
    : `url(#${gradientId})`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 40"
      fill="none"
      width={size}
      height={size}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle", className)}
      {...props}
    >
      {title && <title>{title}</title>}
      <defs>
        {/* Dégradé Corporate Premium (Indigo Royal vers Bleu Électrique Saturé) */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4F46E5" />
          <stop offset="50%" stopColor="#2563EB" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </linearGradient>

        {/* Fond subtil glassmorphism */}
        <linearGradient id={bgGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0.08" />
        </linearGradient>

        <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Cadre Squircle Corporate de précision */}
      <rect
        x="3"
        y="3"
        width="34"
        height="34"
        rx="9"
        fill={isGradient ? `url(#${bgGradientId})` : "none"}
        stroke={isWhite ? "rgba(255,255,255,0.3)" : isMono ? "currentColor" : `url(#${gradientId})`}
        strokeWidth="1.5"
        strokeOpacity={isMono ? "0.3" : "0.5"}
      />

      {/* Bec du "1" */}
      <path
        d="M11 16.5L16 13"
        stroke={strokeColor}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Fût vertical du "1" et jambe gauche du "N" */}
      <path
        d="M16 13V27"
        stroke={strokeColor}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Diagonale du "N" */}
      <path
        d="M16 13.5L25 26.5"
        stroke={strokeColor}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Jambe droite du "N" */}
      <path
        d="M25 27V13"
        stroke={strokeColor}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Nœud de convergence lumineuse (Point Bleu Électrique Saturé) */}
      <circle
        cx="29.5"
        cy="10.5"
        r="2.5"
        fill="#2563EB"
        filter={isGradient ? `url(#${glowId})` : undefined}
      />
    </svg>
  );
}

/**
 * Logo officiel One Nexty (Entreprise / Corporate)
 * Propose les modes Horizontal, Stacked, Full et Monogramme.
 */
export function OneNextyLogo({
  variant = "full",
  color = "primary",
  size = 36,
  title = "One Nexty",
  className,
  ...props
}: OneNextyLogoProps) {
  if (variant === "mark") {
    return <OneNextyMark color={color} size={size} title={title} className={className} {...props} />;
  }

  const isStacked = variant === "stacked";
  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const textColorOne = isWhite ? "text-white" : isMono ? "text-current" : "text-fg dark:text-white";
  const textColorNexty = isWhite ? "text-slate-200" : isMono ? "opacity-80" : "text-fg-muted dark:text-slate-300";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-3 select-none",
        isStacked ? "flex-col text-center gap-2" : "flex-row",
        className
      )}
    >
      <OneNextyMark color={color} size={size} title={title} {...props} />
      <div className={cn("flex flex-col", isStacked ? "items-center" : "items-start")}>
        <div className="flex items-baseline gap-1.5 font-sans tracking-tight">
          <span
            className={cn(
              "font-extrabold uppercase",
              textColorOne,
              size <= 28 ? "text-base" : size <= 40 ? "text-xl" : "text-2xl"
            )}
          >
            ONE
          </span>
          <span
            className={cn(
              "font-light tracking-wide uppercase",
              textColorNexty,
              size <= 28 ? "text-base" : size <= 40 ? "text-xl" : "text-2xl"
            )}
          >
            NEXTY
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Logo Corporate One Nexty Animé (Interactive Hover & Stroke Draw)
 */
export interface AnimatedOneNextyLogoProps extends OneNextyLogoProps {
  /** Active automatiquement l'animation en boucle */
  autoAnimate?: boolean;
}

export function AnimatedOneNextyLogo({
  variant = "full",
  color = "primary",
  size = 40,
  autoAnimate = false,
  className,
  ...props
}: AnimatedOneNextyLogoProps) {
  const [isHovered, setIsHovered] = useState(false);
  const gradientId = useId();
  const bgGradientId = useId();
  const isActive = autoAnimate || isHovered;

  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const textColorOne = isWhite ? "text-white" : isMono ? "text-current" : "text-fg dark:text-white";
  const textColorNexty = isWhite ? "text-slate-200" : isMono ? "opacity-80" : "text-fg-muted dark:text-slate-300";

  const markComponent = (
    <motion.div
      className="relative inline-block cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={springTransition}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40 40"
        fill="none"
        width={size}
        height={size}
        className="overflow-visible"
        {...props}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="50%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#1D4ED8" />
          </linearGradient>
          <linearGradient id={bgGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0.08" />
          </linearGradient>
        </defs>

        {/* Aura lumineuse au survol */}
        <motion.rect
          x="3"
          y="3"
          width="34"
          height="34"
          rx="9"
          fill={`url(#${gradientId})`}
          animate={{
            scale: isActive ? [1, 1.1, 1] : 1,
            opacity: isActive ? 0.25 : 0,
          }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "20px 20px" }}
        />

        {/* Cadre Squircle avec pulsation */}
        <motion.rect
          x="3"
          y="3"
          width="34"
          height="34"
          rx="9"
          fill={`url(#${bgGradientId})`}
          stroke={`url(#${gradientId})`}
          strokeWidth="1.5"
          animate={{
            strokeOpacity: isActive ? 0.9 : 0.4,
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Bec du "1" */}
        <motion.path
          d="M11 16.5L16 13"
          stroke={`url(#${gradientId})`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4 }}
        />

        {/* Fût vertical du "1" */}
        <motion.path
          d="M16 13V27"
          stroke={`url(#${gradientId})`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4 }}
        />

        {/* Diagonale du "N" */}
        <motion.path
          d="M16 13.5L25 26.5"
          stroke={`url(#${gradientId})`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4, delay: 0.1 }}
        />

        {/* Jambe droite du "N" */}
        <motion.path
          d="M25 27V13"
          stroke={`url(#${gradientId})`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4, delay: 0.15 }}
        />

        {/* Point bleu d'accentuation */}
        <motion.circle
          cx="29.5"
          cy="10.5"
          r="2.5"
          fill="#2563EB"
          animate={{
            scale: isActive ? [1, 1.35, 1] : 1,
            opacity: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 1.2, repeat: Infinity }}
          style={{ transformOrigin: "29.5px 10.5px" }}
        />
      </svg>
    </motion.div>
  );

  if (variant === "mark") {
    return markComponent;
  }

  const isStacked = variant === "stacked";

  return (
    <motion.div
      className={cn(
        "inline-flex items-center gap-3 cursor-pointer select-none",
        isStacked ? "flex-col text-center gap-2" : "flex-row",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {markComponent}
      <div className={cn("flex flex-col", isStacked ? "items-center" : "items-start")}>
        <div className="flex items-baseline gap-1.5 font-sans tracking-tight">
          <motion.span
            className={cn("font-extrabold uppercase text-xl", textColorOne)}
            animate={{ color: isActive ? "#4F46E5" : undefined }}
            transition={{ duration: 0.2 }}
          >
            ONE
          </motion.span>
          <motion.span
            className={cn("font-light tracking-wide uppercase text-xl", textColorNexty)}
            animate={{ color: isActive ? "#2563EB" : undefined }}
            transition={{ duration: 0.2 }}
          >
            NEXTY
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
}
